import { isAiOperationsManagerSolutionKey } from './aiOperationsManagerConfig';
import { normalizeSolutionItemTypes } from './itemTypes';

export interface WorkflowTemplateDescriptor {
  templateKey: string;
  templateName: string;
  category?: string;
  itemTypes: string[];
}

interface TemplateSubPageLike {
  key?: string | null;
  component?: string | null;
  label?: string | null;
  icon?: string | null;
  customer_facing?: boolean | null;
  props?: Record<string, unknown> | null;
}

export interface FilterTemplateSubPagesOptions {
  activeSolutionKeys?: readonly string[] | null;
  includeCustomerFacing?: boolean;
  /**
   * Item types currently disabled for the client (e.g. optional types like
   * follow_up that have not been enabled). A sub-page is hidden when it
   * declares a non-empty `props.item_types` scope and every declared type is
   * disabled — its content would be empty anyway.
   */
  disabledItemTypes?: ReadonlySet<string> | null;
}

const REDUNDANT_LEGAL_SUBPAGE_KEYS = new Set(['team', 'contacts']);
const REDUNDANT_LEGAL_SUBPAGE_COMPONENTS = new Set([
  'team_directory',
  'legal_team',
  'contacts',
  'contact_directory',
]);
const REDUNDANT_SERVICE_OPERATIONS_SUBPAGE_KEYS = new Set(['field-workers', 'portfolio', 'costing']);
const REDUNDANT_SERVICE_OPERATIONS_SUBPAGE_COMPONENTS = new Set([
  'project_portfolio',
  'job_costing',
]);
const AI_REVIEW_SERVICE_OPERATIONS_SUBPAGE_KEYS = new Set(['ai_review', 'ai-review']);
const AI_REVIEW_SERVICE_OPERATIONS_SUBPAGE_COMPONENTS = new Set([
  'ai_review_queue',
  'ai_operations_review',
]);
const REDUNDANT_FLEET_SUBPAGE_KEYS = new Set(['billing_submissions', 'billing-submissions']);
const REDUNDANT_FLEET_SUBPAGE_COMPONENTS = new Set(['billing_submission_queue']);
// Access Control unified its identity surface: Tag Holders (client_people) is
// the only people tab — the contacts directory ('people') and the standalone
// 'faces' enrollment panel were folded into the person record's credentials.
const REDUNDANT_ACCESS_CONTROL_SUBPAGE_KEYS = new Set(['people', 'faces']);
const REDUNDANT_ACCESS_CONTROL_SUBPAGE_COMPONENTS = new Set([
  'contact_management_hub',
  'contacts',
  'contact_face_enrollment',
]);

export function filterTemplateSubPages<T extends TemplateSubPageLike>(
  templateKey: string | null | undefined,
  subPages?: T[] | null,
  options?: FilterTemplateSubPagesOptions,
): T[] {
  const includeCustomerFacing = options?.includeCustomerFacing === true;
  const disabledItemTypes = options?.disabledItemTypes;
  const rawPages = Array.isArray(subPages) ? subPages : [];
  const pages = includeCustomerFacing
    ? rawPages.filter((subPage) => subPage?.customer_facing === true)
    : rawPages.filter((subPage) => subPage?.customer_facing !== true)
        .filter((subPage) => {
          if (!disabledItemTypes || disabledItemTypes.size === 0) return true;
          const scopedTypes = normalizeSolutionItemTypes(
            subPage?.props?.item_types ?? subPage?.props?.work_item_type,
          );
          if (scopedTypes.length === 0) return true;
          return !scopedTypes.every((itemType) => disabledItemTypes.has(itemType));
        });
  const normalizedTemplateKey = (templateKey || '').trim();

  if (normalizedTemplateKey === 'legal-services') {
    return pages.filter((subPage) => {
      const key = typeof subPage?.key === 'string' ? subPage.key.trim() : '';
      const component = typeof subPage?.component === 'string' ? subPage.component.trim() : '';

      return !REDUNDANT_LEGAL_SUBPAGE_KEYS.has(key)
        && !REDUNDANT_LEGAL_SUBPAGE_COMPONENTS.has(component);
    });
  }

  if (normalizedTemplateKey === 'fleet-management') {
    return pages.filter((subPage) => {
      const key = typeof subPage?.key === 'string' ? subPage.key.trim() : '';
      const component = typeof subPage?.component === 'string' ? subPage.component.trim() : '';

      return !REDUNDANT_FLEET_SUBPAGE_KEYS.has(key)
        && !REDUNDANT_FLEET_SUBPAGE_COMPONENTS.has(component);
    });
  }

  if (normalizedTemplateKey === 'access-control') {
    return pages.filter((subPage) => {
      const key = typeof subPage?.key === 'string' ? subPage.key.trim() : '';
      const component = typeof subPage?.component === 'string' ? subPage.component.trim() : '';

      return !REDUNDANT_ACCESS_CONTROL_SUBPAGE_KEYS.has(key)
        && !REDUNDANT_ACCESS_CONTROL_SUBPAGE_COMPONENTS.has(component);
    });
  }

  if (normalizedTemplateKey === 'service-operations') {
    const hasAiOperationsManagerActive = (options?.activeSolutionKeys || []).some((solutionKey) =>
      isAiOperationsManagerSolutionKey(solutionKey)
    );

    return pages.filter((subPage) => {
      const key = typeof subPage?.key === 'string' ? subPage.key.trim() : '';
      const component = typeof subPage?.component === 'string' ? subPage.component.trim() : '';

      if (REDUNDANT_SERVICE_OPERATIONS_SUBPAGE_KEYS.has(key)
        || REDUNDANT_SERVICE_OPERATIONS_SUBPAGE_COMPONENTS.has(component)) {
        return false;
      }

      if (!hasAiOperationsManagerActive) {
        return !AI_REVIEW_SERVICE_OPERATIONS_SUBPAGE_KEYS.has(key)
          && !AI_REVIEW_SERVICE_OPERATIONS_SUBPAGE_COMPONENTS.has(component);
      }

      return true;
    });
  }

  return pages;
}

export function normalizeWorkflowTemplateDescriptors(rawTemplates: unknown): WorkflowTemplateDescriptor[] {
  if (!Array.isArray(rawTemplates)) {
    return [];
  }

  const templates = new Map<string, WorkflowTemplateDescriptor>();

  rawTemplates.forEach((rawTemplate) => {
    if (!rawTemplate || typeof rawTemplate !== 'object') {
      return;
    }

    const template = rawTemplate as Record<string, unknown>;
    const templateKey = typeof template.templateKey === 'string'
      ? template.templateKey.trim()
      : typeof template.template_key === 'string'
        ? template.template_key.trim()
        : '';

    if (!templateKey) {
      return;
    }

    const templateName = typeof template.templateName === 'string' && template.templateName.trim()
      ? template.templateName.trim()
      : typeof template.template_name === 'string' && template.template_name.trim()
        ? template.template_name.trim()
        : templateKey;

    const itemTypes = normalizeSolutionItemTypes(
      template.itemTypes
      ?? template.item_types
      ?? template.appliesToItemType
      ?? template.applies_to_item_type,
    );

    templates.set(templateKey, {
      templateKey,
      templateName,
      category: typeof template.category === 'string' ? template.category : undefined,
      itemTypes,
    });
  });

  return Array.from(templates.values());
}